<template>
  <ViewerLayoutRoot class="content-owner-layout" data-testid="content-owner-layout">
    <ContentOwnerTopBar />

    <div class="content-owner-layout__content">
      <div ref="contentRoot" data-testid="content-owner-content">
        <ViewerContent :max-height="contentMaxHeight" />
      </div>
      <div v-if="isMobile" class="px-4 pt-4 pb-1">
        <ItemName />
      </div>
      <div ref="metadataRoot" class="px-4 sm:px-0" data-testid="content-owner-metadata">
        <MetadataSection />
      </div>
    </div>

    <div class="content-owner-layout__tabs px-4 sm:px-0">
      <ContentOwnerTabs @added-comment="emit('addedComment')" @navigation-ready="onNavigationReady" />
    </div>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import { useMobileDetection } from '@/composables/useMobileDetection';
import ContentOwnerTopBar from './ContentOwnerTopBar.vue';
import ContentOwnerTabs from './ContentOwnerTabs.vue';
import MetadataSection from '../../shared/MetadataSection.vue';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';

const MIN_CONTENT_HEIGHT_PX = 320;

const { isMobile } = useMobileDetection();
const contentMaxHeight = ref<string>();
const contentRoot = ref<HTMLElement | null>(null);
const metadataRoot = ref<HTMLElement | null>(null);
let tabNavigation: HTMLElement | null = null;
let controlsResizeObserver: ResizeObserver | undefined;
let resizeRafId: number | undefined;

const emit = defineEmits<{
  addedComment: [];
}>();

const updateContentMaxHeight = () => {
  if (isMobile.value || !contentRoot.value || !metadataRoot.value || !tabNavigation) {
    contentMaxHeight.value = undefined;
    return;
  }

  const available =
    window.innerHeight -
    contentRoot.value.getBoundingClientRect().top -
    metadataRoot.value.getBoundingClientRect().height -
    tabNavigation.getBoundingClientRect().height -
    24;

  // Never shrink below MIN_CONTENT_HEIGHT_PX — on very short viewports the page scrolls to reach
  // the tab navigation instead of squeezing the image past legibility.
  contentMaxHeight.value = `${Math.max(MIN_CONTENT_HEIGHT_PX, Math.floor(available))}px`;
};

const scheduleContentMaxHeightUpdate = () => {
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
  resizeRafId = requestAnimationFrame(updateContentMaxHeight);
};

const observeControls = () => {
  if (isMobile.value || !window.ResizeObserver || !metadataRoot.value || !tabNavigation) {
    return;
  }

  controlsResizeObserver?.disconnect();
  controlsResizeObserver = new ResizeObserver(scheduleContentMaxHeightUpdate);
  controlsResizeObserver.observe(metadataRoot.value);
  controlsResizeObserver.observe(tabNavigation);
};

const onNavigationReady = (element: HTMLElement) => {
  tabNavigation = element;
  updateContentMaxHeight();
  observeControls();
};

watch(isMobile, mobile => {
  if (mobile) {
    contentMaxHeight.value = undefined;
    controlsResizeObserver?.disconnect();
    return;
  }

  scheduleContentMaxHeightUpdate();
  observeControls();
});

onMounted(async () => {
  await nextTick();
  updateContentMaxHeight();
  window.addEventListener('resize', scheduleContentMaxHeightUpdate);
});

onUnmounted(() => {
  window.removeEventListener('resize', scheduleContentMaxHeightUpdate);
  controlsResizeObserver?.disconnect();
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
});
</script>

<style scoped>
.content-owner-layout__content {
  @apply flex flex-col min-w-0;
}

.content-owner-layout__tabs {
  @apply mt-2;
}
</style>
